// NOTE: This histogram + KDE is styled to mimic matplotlib/seaborn statistical plots in React.
import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, ComposedChart, Legend, Area } from 'recharts';
import GoogleSheetsApiService from '../services/googleSheetsApi';
import { getAvatarForUser } from '../lib/wrapped/avatarUtils';

/**
 * Wingonomics page: League-wide WINGO stats and charts
 * - Total WINGO balance by day (all categories)
 * - Total WINGO mined by day (category == 'Mining')
 * - Wingi coefficient (Lorenz curve)
 * - WINGOs per session: histogram + KDE styled like seaborn/matplotlib
 */

interface LogRow {
  date: string; // column C
  amount: number; // column D
  user: string; // column B
  category: string; // column K
}

const GOOGLE_SHEET_LOG_URL = 'https://docs.google.com/spreadsheets/d/1eWkRJZph0X75PXfyWd22iKtgDsQhiv7RrH9X8xDKwu4/gviz/tq?tqx=out:csv&gid=270601813';

// Helper to parse M/D/YY to ISO string
function parseMDYYtoISO(dateStr: string): string {
  // e.g. 5/28/25 or 12/31/25
  const [m, d, y] = dateStr.split('/');
  if (!m || !d || !y) return '';
  // Assume 20xx for 2-digit year
  const yyyy = y.length === 2 ? '20' + y.padStart(2, '0') : y;
  // Pad month and day
  const mm = m.padStart(2, '0');
  const dd = d.padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`; // No time component
}

const Wingonomics: React.FC = () => {
  const [log, setLog] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartHeight, setChartHeight] = useState(300);

  // Chart data
  const [balanceByDay, setBalanceByDay] = useState<any[]>([]);
  const [minedByDay, setMinedByDay] = useState<any[]>([]);
  const [lorenzData, setLorenzData] = useState<any[]>([]);
  const [wingiCoefficient, setWingiCoefficient] = useState<number | null>(null);
  const [histogramData, setHistogramData] = useState<{ bin: number; count: number; density: number; label: string }[]>([]);
  const [sessionStats, setSessionStats] = useState<{ mean: number; median: number; std: number }>({ mean: 0, median: 0, std: 0 });
  const [boxPlotData, setBoxPlotData] = useState<any[]>([]);
  const [kdeData, setKdeData] = useState<{ x: number; y: number }[]>([]);

  // Responsive chart height
  useEffect(() => {
    const updateHeight = () => {
      setChartHeight(window.innerWidth >= 768 ? 500 : 220);
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  useEffect(() => {
    // Fetch and parse the Google Sheet log
    const fetchData = async () => {
      try {
        const sheetsService = GoogleSheetsApiService.getInstance();
        const rows = await sheetsService.fetchSheetAsCSV('Log');
        const data = rows.slice(1); // skip header
        
        // Debug: log the first few raw rows
        console.log('First 3 raw CSV rows:', data.slice(0, 3));
        
        const parsed: LogRow[] = data
          .filter(row => row.length >= 13)
          .map((row) => {
            const rawDate = row[2]?.trim() || '';
            const isoDate = rawDate ? parseMDYYtoISO(rawDate) : '';
            console.log('[WINGONOMICS DEBUG] Raw:', rawDate, '| ISO:', isoDate);
            return {
              user: row[1]?.trim() || '', // column B
              date: isoDate, // column C, just the date
              amount: Number(row[3]) || 0, // column D (WINGO +/-)
              category: row[10]?.trim() || '', // column K
            };
          })
          .filter(entry => entry.user && entry.date);
        
        // Debug: log the first few parsed entries
        console.log('First 3 parsed entries:', parsed.slice(0, 3));
        
        setLog(parsed);
        setLoading(false);
        setError(null);
      } catch (err: any) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Compute chart data when log is loaded
  useEffect(() => {
    if (!log.length) return;
    
    // Debug: log the first few entries to see the data format
    console.log('First 5 log entries:', log.slice(0, 5));
    
    // 1. Prepare cumulative data for both balance and mined by aggregating by date
    const sorted = [...log].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    // Group by date and sum amounts
    const dailyData: Record<string, { balance: number; mined: number; hasActivity: boolean }> = {};
    let runningTotal = 0;
    
    // First pass: collect all activity by date
    sorted.forEach(row => {
      // Use the date string directly - no Date object conversion
      const dateStr = row.date; // Already in YYYY-MM-DD format
      
      if (!dailyData[dateStr]) {
        dailyData[dateStr] = { balance: 0, mined: 0, hasActivity: false };
      }
      
      dailyData[dateStr].hasActivity = true;
      
      if (row.category === 'Mining') {
        dailyData[dateStr].mined += row.amount;
      }
    });
    
    // Second pass: calculate cumulative totals only for dates with activity
    const sortedDates = Object.keys(dailyData).sort();
    sortedDates.forEach(date => {
      if (dailyData[date].hasActivity) {
        // Sum all transactions up to this date
        const transactionsUpToDate = sorted.filter(row => {
          // Use date string comparison directly - no Date object conversion
          const rowDate = row.date; // Already in YYYY-MM-DD format
          return rowDate <= date;
        });
        runningTotal = transactionsUpToDate.reduce((sum, row) => sum + row.amount, 0);
        dailyData[date].balance = runningTotal;
      }
    });
    
    // Debug: log the daily data
    console.log('Daily data:', dailyData);
    
    // Convert to cumulative mined totals - only accumulate when there's actual mining activity
    let cumulativeMined = 0;
    sortedDates.forEach(date => {
      if (dailyData[date].mined > 0) {
        cumulativeMined += dailyData[date].mined;
      }
      dailyData[date].mined = cumulativeMined;
    });
    
    // Fill in gaps between dates - start from 5/26/25 as requested
    const startDateStr = '2025-05-26';
    const endDateStr = sortedDates[sortedDates.length - 1];
    const filledData: { date: string; balance: number; mined: number; timestamp: number }[] = [];
    
    let lastBalance = 0;
    let lastMined = 0;
    let firstActivityDate = null;
    
    // Find the first date with activity
    for (const date of sortedDates) {
      if (dailyData[date].hasActivity) {
        firstActivityDate = date;
        break;
      }
    }
    
    // Simple date incrementing without Date objects
    let currentDateStr = startDateStr;
    while (currentDateStr <= endDateStr) {
      if (dailyData[currentDateStr] && dailyData[currentDateStr].hasActivity) {
        lastBalance = dailyData[currentDateStr].balance;
        lastMined = dailyData[currentDateStr].mined;
      }
      
      // Create timestamp for chart (noon on the date)
      const timestamp = new Date(currentDateStr + 'T12:00:00').getTime();
      
      filledData.push({
        date: currentDateStr,
        balance: firstActivityDate && currentDateStr >= firstActivityDate ? lastBalance : 0,
        mined: firstActivityDate && currentDateStr >= firstActivityDate ? lastMined : 0,
        timestamp: timestamp
      });
      
      // Debug logging for 7/17 and 7/18
      if (currentDateStr === '2025-07-17' || currentDateStr === '2025-07-18') {
        console.log(`Date: ${currentDateStr}, Timestamp: ${timestamp}, Balance: ${firstActivityDate && currentDateStr >= firstActivityDate ? lastBalance : 0}, Mined: ${firstActivityDate && currentDateStr >= firstActivityDate ? lastMined : 0}`);
      }
      
      // Increment date string
      const [year, month, day] = currentDateStr.split('-').map(Number);
      const nextDate = new Date(year, month - 1, day + 1);
      currentDateStr = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(nextDate.getDate()).padStart(2, '0')}`;
    }
    
    // Debug: log the final data
    console.log('Final filled data:', filledData.slice(0, 5));
    console.log('Last few filled data entries:', filledData.slice(-5));
    
    setBalanceByDay(filledData);

    // 2. Lorenz curve (Wingi coefficient)
    // Compute user balances
    const userBalances: Record<string, number> = {};
    sorted.forEach(row => {
      if (!userBalances[row.user]) userBalances[row.user] = 0;
      userBalances[row.user] += row.amount;
    });
    const balances = Object.values(userBalances).sort((a, b) => a - b);
    const nBalances = balances.length;
    let cumSum = 0;
    const lorenz: { pctUsers: number; pctWingo: number }[] = [];
    const total = balances.reduce((a, b) => a + b, 0);
    
    // Add starting point (0,0) to ensure lines touch bottom-left corner
    lorenz.push({ pctUsers: 0, pctWingo: 0 });
    
    balances.forEach((bal, i) => {
      cumSum += bal;
      lorenz.push({
        pctUsers: (i + 1) / nBalances,
        pctWingo: total ? cumSum / total : 0
      });
    });
    
    // Add the 45-degree equality line with (0,0) starting point
    const equalityLine = Array.from({ length: lorenz.length }, (_, i) => ({
      pctUsers: lorenz[i].pctUsers,
      equality: lorenz[i].pctUsers
    }));
    
    // Merge equality line into lorenzData for charting
    const lorenzWithEquality = lorenz.map((point, i) => ({ 
      ...point, 
      equality: equalityLine[i].equality
    }));
    setLorenzData(lorenzWithEquality);
    
    // Wingi coefficient = 1 - 2 * area under Lorenz curve
    let area = 0;
    for (let i = 1; i < lorenz.length; i++) {
      area += (lorenz[i].pctUsers - lorenz[i - 1].pctUsers) * (lorenz[i].pctWingo + lorenz[i - 1].pctWingo) / 2;
    }
    setWingiCoefficient(1 - 2 * area);

    // 3. WINGOs per session (distribution for Mining sessions, grouped bins for histogram look)
    const miningSessions = sorted.filter(row => row.category === 'Mining').map(row => row.amount);
    const nSessions = miningSessions.length;
    const mean = nSessions ? miningSessions.reduce((a, b) => a + b, 0) / nSessions : 0;
    const sortedSessions = [...miningSessions].sort((a, b) => a - b);
    const median = nSessions ? (nSessions % 2 === 1 ? sortedSessions[Math.floor(nSessions / 2)] : (sortedSessions[nSessions / 2 - 1] + sortedSessions[nSessions / 2]) / 2) : 0;
    const std = nSessions ? Math.sqrt(miningSessions.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / nSessions) : 0;
    setSessionStats({ mean, median, std });

    // --- Histogram bins (optimal number of equal-width bins) and KDE density for ComposedChart ---
    const minVal = 1; // Start from 1, not 0
    const maxVal = Math.ceil(Math.max(...miningSessions, 1));
    
    // Calculate optimal number of bins using Sturges' formula: k = 1 + 3.322 * log10(n)
    // But cap it between 6 and 8 for readability
    const n = miningSessions.length;
    const sturgesBins = Math.ceil(1 + 3.322 * Math.log10(n));
    const numBins = Math.max(6, Math.min(8, sturgesBins));
    
    const binWidth = (maxVal - minVal + 1) / numBins;
    const bins: { bin: number; count: number; density: number; label: string }[] = [];
    
    // 1. Histogram binning with equal-width bins
    for (let i = 0; i < numBins; i++) {
      const start = minVal + i * binWidth;
      const end = minVal + (i + 1) * binWidth - 1;
      const count = miningSessions.filter(v => v >= start && v <= end).length;
      
      // Ensure the last bin shows the actual max value
      const actualEnd = i === numBins - 1 ? maxVal : Math.floor(end);
      const label = `${Math.floor(start)}-${actualEnd}`;
      
      bins.push({ bin: start + binWidth / 2, count, density: 0, label });
    }
    // 2. KDE density estimation at bin centers
    if (miningSessions.length > 0) {
      const bandwidth = Math.max(0.8, (maxVal - minVal) / 20);
      function kernel(u: number) {
        return Math.exp(-0.5 * u * u) / Math.sqrt(2 * Math.PI);
      }
      bins.forEach((b, i) => {
        const x = b.bin;
        const sum = miningSessions.reduce((acc, xi) => acc + kernel((x - xi) / bandwidth), 0);
        bins[i].density = sum / (miningSessions.length * bandwidth);
      });
    }
    console.log('Histogram bins:', bins);
    setHistogramData(bins);

    // --- KDE Curve Calculation ---
    // 1. Get mining session values
    const miningSessionsKde = sorted.filter(row => row.category === 'Mining').map(row => row.amount);
    if (miningSessionsKde.length > 0) {
      // 2. KDE parameters
      const minX = Math.min(...miningSessionsKde);
      const maxX = Math.max(...miningSessionsKde);
      const bandwidth = Math.max(0.8, (maxX - minX) / 20); // Silverman's rule of thumb, but capped
      const steps = 60;
      const xs: number[] = [];
      for (let i = 0; i <= steps; i++) {
        xs.push(minX + (maxX - minX) * (i / steps));
      }
      // 3. Gaussian kernel
      function kernel(u: number) {
        return Math.exp(-0.5 * u * u) / Math.sqrt(2 * Math.PI);
      }
      // 4. KDE estimation
      const kde: { x: number; y: number }[] = xs.map(x => {
        const sum = miningSessionsKde.reduce((acc, xi) => acc + kernel((x - xi) / bandwidth), 0);
        return { x, y: sum / (miningSessionsKde.length * bandwidth) };
      });
      setKdeData(kde);
    } else {
      setKdeData([]);
    }
  }, [log]);

  if (loading) return <div>Loading Wingonomics data...</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;

  return (
    <div className="max-w-4xl mx-auto py-8">
      {/* Top 3 WINGO Holders Avatars */}
      <div className="flex justify-start md:justify-end mb-4">
        <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg p-2 shadow-sm">
          <div className="flex items-center gap-1 md:gap-2">
            {(() => {
              const userBalances: Record<string, number> = {};
              log.forEach(row => {
                if (!userBalances[row.user]) userBalances[row.user] = 0;
                userBalances[row.user] += row.amount;
              });
              const sortedUsers = Object.entries(userBalances).sort(([,a], [,b]) => b - a);
              const top3 = sortedUsers.slice(0, 3);
              
              return top3.map(([username, balance], index) => {
                const borderColors = ['border-yellow-400', 'border-gray-400', 'border-amber-600']; // Gold, Silver, Bronze
                const borderWidths = ['border-3', 'border-2', 'border-2'];
                const medalEmojis = ['🥇', '🥈', '🥉'];
                
                return (
                  <div key={username} className="flex flex-col items-center group">
                    <div className="relative">
                      <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden ${borderColors[index]} ${borderWidths[index]} bg-white shadow-md group-hover:scale-110 transition-transform duration-200`}>
                        <img 
                          src={getAvatarForUser(username)} 
                          alt={username}
                          className="w-full h-full object-cover"
                          style={{ objectPosition: 'center 25%' }}
                        />
                      </div>
                      <div className="absolute -top-1 -right-1 text-xs">
                        {medalEmojis[index]}
                      </div>
                    </div>
                    <div className="text-xs font-bold text-gray-800 mt-1 text-center min-w-0 px-1">
                      <span className="block truncate max-w-20 md:max-w-24">{username}</span>
                    </div>
                    <div className="text-xs text-gray-500 font-medium">
                      {balance.toLocaleString()} W
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      <h1 className="text-3xl font-bold mb-6 text-wingo-600">Wingonomics</h1>

      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-2">World Wide WINGO</h2>
        <div className="relative">
          <div className="absolute top-12 left-20 md:left-20 z-10">
            <div className="inline-flex flex-col md:flex-row items-start md:items-center max-w-fit px-2 py-0.5 bg-white/95 border border-gray-300 shadow-md rounded-md text-xs gap-1 md:gap-3 backdrop-blur-sm">
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">W Circulating:</span>
                <span className="font-semibold text-gray-900">{balanceByDay.length > 0 ? balanceByDay[balanceByDay.length - 1].balance.toLocaleString() : '0'}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">W Mined:</span>
                <span className="font-semibold text-gray-900">{balanceByDay.length > 0 ? balanceByDay[balanceByDay.length - 1].mined.toLocaleString() : '0'}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">Leader:</span>
                <span className="font-semibold text-gray-900">
                  {(() => {
                    const userBalances: Record<string, number> = {};
                    log.forEach(row => {
                      if (!userBalances[row.user]) userBalances[row.user] = 0;
                      userBalances[row.user] += row.amount;
                    });
                    const sortedUsers = Object.entries(userBalances).sort(([,a], [,b]) => b - a);
                    return sortedUsers.length > 0 ? sortedUsers[0][0] : 'N/A';
                  })()}
                </span>
              </div>
            </div>
          </div>
          

          
          <div className="h-[300px] md:h-[600px] [&_*]:focus:outline-none [&_*]:focus:ring-0 [&_*]:select-none [&_svg]:focus:outline-none [&_svg]:focus:ring-0" style={{ userSelect: 'none' }}>
            <ResponsiveContainer width="100%" height="100%" style={{ outline: 'none' }}>
              <LineChart data={balanceByDay} margin={{ top: 20, right: 30, left: 0, bottom: 0 }} style={{ outline: 'none', userSelect: 'none' }}>
                <CartesianGrid strokeDasharray="3 3" />
                {/* X-axis: use timestamps for proper time scaling */}
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={timestamp => {
                    const d = new Date(timestamp);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                  minTickGap={3}
                  tick={{ fontSize: 10 }}
                  className="md:minTickGap-10 md:tick-fontSize-12"
                  ticks={(() => {
                    // Generate ticks every 7 days starting from 5/28/2025
                    const startDate = new Date('2025-05-28T12:00:00').getTime();
                    
                    // Find the most recent WINGO logging date
                    const lastWingoDateStr = balanceByDay.length > 0 ? 
                      balanceByDay[balanceByDay.length - 1].date : 
                      '2025-05-28';
                    
                    // Find the most recent Wednesday (or the last WINGO date if it's a Wednesday)
                    const lastWingoDate = new Date(lastWingoDateStr + 'T12:00:00');
                    const lastWingoDay = lastWingoDate.getDay(); // 0=Sunday, 3=Wednesday
                    let lastTickDate;
                    if (lastWingoDay === 3) { // If last WINGO was on a Wednesday
                      lastTickDate = lastWingoDate.getTime();
                    } else {
                      // Find the most recent Wednesday before or on the last WINGO date
                      const daysSinceWednesday = (lastWingoDay + 4) % 7; // Days since last Wednesday
                      lastTickDate = lastWingoDate.getTime() - (daysSinceWednesday * 24 * 60 * 60 * 1000);
                    }
                    
                    const ticks = [];
                    let currentDate = startDate;
                    while (currentDate <= lastTickDate) {
                      ticks.push(currentDate);
                      currentDate += 7 * 24 * 60 * 60 * 1000; // Add 7 days in milliseconds
                    }
                    console.log('Generated ticks:', ticks.map(t => new Date(t).toLocaleDateString()));
                    return ticks;
                  })()}
                />
                <YAxis 
                  label={{ value: 'WINGO', angle: -90, position: 'insideLeft', offset: 10 }} 
                  tick={{ fontSize: 10 }}
                  className="md:tick-fontSize-12"
                />
                <Tooltip labelFormatter={timestamp => {
                  const d = new Date(timestamp as number);
                  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
                }} contentStyle={{ lineHeight: '1.0' }} />
                <Legend wrapperStyle={{ lineHeight: '1.2' }} />
                <Line type="monotone" dataKey="balance" name="Circulating" stroke="#E6C200" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="mined" name="Mined" stroke="#00bcd4" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="relative">
            <div className="absolute bottom-0 right-6 bg-gray-900 text-white rounded-md px-2 py-0 text-sm font-medium">
              D<span className="!text-[#00bcd4] font-semibold">AI</span>SY™
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl font-semibold mb-2">WINGO Distribution</h2>
        <div className="relative">
          <div className="absolute top-1/2 transform -translate-y-1/2 -rotate-90 text-gray-600 z-10 md:left-[-60px] left-[-60px]" style={{ top: '50%' }}>WINGO Holdings</div>
          <ResponsiveContainer width="100%" height={400} className="md:h-[400px] h-[150px]" style={{ outline: 'none' }}>
            <LineChart data={lorenzData} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              {/* X and Y axes: start at 0, custom labels, more padding */}
              <XAxis 
                dataKey="pctUsers" 
                domain={[0, 1]} 
                type="number" 
                tickFormatter={(v: number) => `${Math.round(v * 100)}%`} 
                tick={{ fontSize: 8 }}
                className="md:tick-fontSize-10"
              />
              <YAxis 
                domain={[0, 1]} 
                tickFormatter={(v: number) => v === 0 ? '' : `${Math.round(v * 100)}%`} 
                tick={{ fontSize: 8 }}
                className="md:tick-fontSize-10"
              />
              <Tooltip
                content={({ payload }) => {
                  if (!payload || payload.length < 2) return null;
                  const pctUsers = payload[0].payload.pctUsers;
                  const pctWingo = payload[0].payload.pctWingo;
                  const usersPct = Math.round((1 - pctUsers) * 100);
                  const wingoPct = ((1 - pctWingo) * 100).toFixed(1);
                  if (usersPct === 0) return null;
                  return (
                    <div style={{
                      fontSize: '11px',
                      color: '#444',
                      padding: '6px 10px',
                      background: 'rgba(255,255,255,0.95)',
                      borderRadius: '7px',
                      boxShadow: '0 1px 6px 0 rgba(0,0,0,0.07)',
                      marginLeft: 32,
                      minWidth: 120
                    }}>
                      <div>The top {usersPct}% hold</div>
                      <div>{wingoPct}% of WINGO.</div>
                    </div>
                  );
                }}
              />
              {/* Shade the area between equality line and Lorenz curve */}
              <Area 
                type="monotone" 
                dataKey="equality" 
                stroke="none" 
                fill="#E6C200" 
                fillOpacity={0.15}
                name=""
              />
              <Area 
                type="monotone" 
                dataKey="pctWingo" 
                stroke="none" 
                fill="#E6C200" 
                fillOpacity={0.05}
                name=""
              />
              <Line type="monotone" dataKey="pctWingo" stroke="#E6C200" strokeWidth={2} dot={false} name="Lorenz Curve" />
              <Line type="monotone" dataKey="equality" stroke="#888" strokeWidth={2} dot={false} name="Equality Line" />
            </LineChart>
          </ResponsiveContainer>
          {/* Add text annotation for Wingi Index in a shaded box - positioned within chart */}
          <div className="absolute top-16 bg-yellow-500 bg-opacity-90 rounded p-3 text-white md:left-40 md:text-base left-20 text-sm">
            <div className="font-bold">
              Wingi Index: {wingiCoefficient !== null ? wingiCoefficient.toFixed(2) : 'N/A'}
            </div>
            <div className="text-xs">
              {wingiCoefficient !== null ? 
                (wingiCoefficient <= 0.10 ? '🐎🌈 Wingotopia (Perfectly Balanced)' :
                 wingiCoefficient <= 0.25 ? '🏇🦝 Stable Market (Mostly Balanced)' :
                 wingiCoefficient <= 0.40 ? '🏇📈 Free-Rein Market (Mild Imbalance)' :
                 wingiCoefficient <= 0.60 ? '🐋🏇 Occupy Wingate (Growing Gap)' :
                 wingiCoefficient <= 0.80 ? '🎩💰 Monopony Alert (Queen DAISY)' :
                 'WINGO Monarchy') : '🐎🤖🪽8️⃣'}
            </div>
          </div>
          <div className="text-center text-gray-600 ml-4" style={{ position: 'relative', top: '-8px' }}>% 320 Population</div>
        </div>
        <div className="relative">
          <div className="absolute bottom-2 right-6 bg-gray-900 text-white rounded-md px-2 py-0 text-sm font-medium">
            D<span className="!text-[#00bcd4] font-semibold">AI</span>SY™
          </div>
        </div>
      </section>

      <section className="mb-2">
        <h2 className="text-xl font-semibold mb-2">WINGO per Session</h2>
        <div className="relative">
          <div className="absolute top-12 left-16 md:left-20 z-10">
            <div className="inline-flex flex-col md:flex-row items-start md:items-center max-w-fit px-2 py-0.5 bg-white/90 border border-gray-200 shadow-sm rounded text-xs gap-1 md:gap-3">
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">Mean:</span>
                <span className="font-semibold text-gray-900">{sessionStats.mean.toFixed(1)}</span>
                <span className="text-gray-500 ml-1">(σ = {sessionStats.std.toFixed(1)})</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">Median:</span>
                <span className="font-semibold text-gray-900">{sessionStats.median.toFixed(1)}</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={chartHeight} style={{ outline: 'none' }}>
            {/* ComposedChart: histogram bars touch, KDE curve overlays */}
            <ComposedChart data={histogramData} margin={{ top: 30, right: 30, left: 0, bottom: 10 }}>
              <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
              {/* X-axis: show all bins, label bins as '1-4', '5-8', ... */}
              <XAxis
                dataKey="bin"
                tick={{ fontSize: window.innerWidth >= 768 ? 10 : 8 }}
                type="category"
                domain={['dataMin', 'dataMax']}
                allowDecimals={false}
                tickFormatter={(bin) => {
                  const binData = histogramData.find(b => b.bin === bin);
                  return binData ? binData.label : String(bin);
                }}
                className="md:tick-fontSize-14"
                interval={0}
                minTickGap={20}
              />
              <YAxis 
                yAxisId="left" 
                allowDecimals={false} 
                label={{ value: 'Sessions', angle: -90, position: 'insideLeft', offset: 10, dy: 20 }} 
                tick={{ fontSize: 10 }}
                className="md:tick-fontSize-14"
              />
              <YAxis yAxisId="right" orientation="right" hide />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'KDE Curve') {
                    return [Number(value).toFixed(2), name];
                  }
                  if (name === 'Sessions') {
                    return [value, name];
                  }
                  return [value, name];
                }}
                labelFormatter={(value) => {
                  // Find the bin that contains this value
                  const bin = histogramData.find(b => b.bin === value);
                  return bin ? bin.label + ' W' : value;
                }}
                contentStyle={{ lineHeight: '1.0' }}
              />
              <Bar yAxisId="left" dataKey="count" fill="#4F8EF7" fillOpacity={0.85} name="Sessions" barSize={100} />
              <Line yAxisId="right" type="monotone" dataKey="density" stroke="#ff7300" strokeWidth={3} dot={false} name="KDE Curve" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="text-center text-gray-600" style={{ position: 'relative', top: '-8px' }}>WINGO Mined</div>
        <div className="relative">
          <div className="absolute bottom-2 right-6 bg-gray-900 text-white rounded-md px-2 py-0 text-sm font-medium">
            D<span className="!text-[#00bcd4] font-semibold">AI</span>SY™
          </div>
        </div>
      </section>
      {/* WINGO to Wingo Chart Section */}
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-1">WINGO Price Chart</h2>
        {/* Tabs for time range */}
        <WingoToWingoChart />
      </section>
      {/* DAISY Maths Explained Link at Bottom */}
      <div className="text-center mt-2 mb-1">
        <a href="/daisy_math" className="text-wingo-600 hover:text-wingo-700 text-sm italic font-normal no-underline">DAISY Maths Explained</a>
      </div>
    </div>
  );
};

// --- WINGO to Wingo Chart Component ---
const WINGO_TO_WINGO_START = new Date('2025-05-28T00:00:00');
const WINGO_TO_WINGO_TABS = [
  { label: '1H', value: '1H' },
  { label: '24H', value: '24H' },
  { label: '1W', value: '1W' },
  { label: '1M', value: '1M' },
  { label: 'All', value: 'ALL' },
];

function getWingoToWingoData(range: string) {
  const now = new Date();
  let start;
  let intervalMs;
  switch (range) {
    case '1H':
      start = new Date(now.getTime() - 60 * 60 * 1000); // 1 hour ago
      intervalMs = 5 * 60 * 1000; // 5 minutes
      break;
    case '24H':
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000); // 24 hours ago
      intervalMs = 60 * 60 * 1000; // 1 hour
      break;
    case '1W':
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); 
      intervalMs = 24 * 60 * 60 * 1000; 
      break;
    case '1M':
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); 
      intervalMs = 24 * 60 * 60 * 1000; 
      break;
    case 'ALL':
    default:
      start = WINGO_TO_WINGO_START; 
      intervalMs = 24 * 60 * 60 * 1000; 
      break;
  }
  if (start < WINGO_TO_WINGO_START) start = WINGO_TO_WINGO_START;
  const data = [];
  let t = new Date(start);
  while (t <= now) {
    data.push({
      date: t.toISOString().slice(0, 10) + (intervalMs < 24 * 60 * 60 * 1000 ? ' ' + t.toTimeString().slice(0, 5) : ''),
      value: 1,
    });
    t = new Date(t.getTime() + intervalMs);
  }
  return data;
}

const WingoToWingoChart: React.FC = () => {
  const [tab, setTab] = React.useState('ALL');
  const data = React.useMemo(() => getWingoToWingoData(tab), [tab]);
  // Calculate today's change (always 0 for this chart)
  const todayChange = 0;
  const todayChangePct = 0.0;
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 relative">
      {/* Title Section */}
      <div className="mb-2 flex flex-col md:flex-row md:items-end md:justify-between gap-1 md:gap-0">
        <div>
          <div className="text-base font-semibold text-gray-900 leading-tight">Market Summary &gt; <span className="text-wingo-600">WINGO</span></div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-gray-900">1.00 <span className="text-[#E6C200] font-bold">W</span> <span className="text-xs text-gray-500">(320m)</span></span>
            <span className="text-green-600 text-sm font-semibold">+ 0.00 (0.0%) today</span>
          </div>
        </div>
      </div>
      {/* Tabs for time range */}
      <div className="flex gap-2 mb-2">
        {WINGO_TO_WINGO_TABS.map(t => (
          <button
            key={t.value}
            className={`px-2 py-1 rounded text-xs font-medium border ${tab === t.value ? 'bg-wingo-600 text-white border-wingo-600' : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'}`}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
          {/* X-axis: MM-DD-YY */}
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 10 }}
            tickFormatter={d => {
              // Handle both date-only (YYYY-MM-DD) and date-time (YYYY-MM-DD HH:MM) formats
              if (d.includes(' ')) {
                // Date-time format: "2025-07-24 17:54" -> "17:54"
                const [datePart, timePart] = d.split(' ');
                return timePart;
              } else {
                // Date-only format: "2025-07-24" -> "07-24-25"
                const [yyyy, mm, dd] = d.split('-');
                return `${mm}-${dd}-${yyyy.slice(2)}`;
              }
            }}
          />
          {/* Y-axis with label, visible axis line, and tick at 1 */}
          <YAxis 
            domain={[0.98, 1.02]} 
            tick={{ fontSize: 10, fill: '#E6C200' }}
            axisLine={true}
            ticks={[1]}
            tickFormatter={v => v === 1 ? '1' : ''}
            label={{ value: 'W per Wingo (320m)', angle: -90, position: 'insideLeft', offset: 10, dy: 40, fill: '#E6C200', fontSize: 12 }}
          />
          {/* Custom Tooltip: just date and 'WINGO = Wingo (320m)' */}
          <Tooltip 
            content={({ label }) => {
              if (!label) return null;
              const labelStr = String(label);
              
              // Handle both date-only (YYYY-MM-DD) and date-time (YYYY-MM-DD HH:MM) formats
              let displayDate;
              if (labelStr.includes(' ')) {
                // Date-time format: "2025-07-24 17:54" -> "17:54 07-24-25"
                const [datePart, timePart] = labelStr.split(' ');
                const [yyyy, mm, dd] = datePart.split('-');
                displayDate = `${timePart} ${mm}-${dd}-${yyyy.slice(2)}`;
              } else {
                // Date-only format: "2025-07-24" -> "07-24-25"
                const [yyyy, mm, dd] = labelStr.split('-');
                displayDate = `${mm}-${dd}-${yyyy.slice(2)}`;
              }
              
              return (
                <div style={{ fontSize: '11px', color: '#222', background: 'rgba(255,255,255,0.97)', borderRadius: 6, padding: '6px 12px', boxShadow: '0 1px 6px 0 rgba(0,0,0,0.07)' }}>
                  <div>{displayDate}</div>
                  <div>1 WINGO = 1 Wingo (320m)</div>
                </div>
              );
            }}
          />
          {/* Orange-ish transparent area fill under the line */}
          <Area type="monotone" dataKey="value" stroke="none" fill="#FFA726" fillOpacity={0.18} />
          <Line type="monotone" dataKey="value" stroke="#E6C200" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex justify-center items-center gap-2 mt-1 mb-1 w-full ml-6">
        <span className="text-xs text-gray-500">1 WINGO = 1 Wingo (320m)</span>
        <span className="bg-gray-900 text-white rounded-md px-2 py-0 text-sm font-medium relative" style={{ top: '-2px' }}>
        D<span className="!text-[#00bcd4] font-semibold">AI</span>SY™
        </span>
      </div>
    </div>
  );
};

export default Wingonomics;